import { Badge, Box } from '@mui/material';
import { ButtonUI, CheckboxUI, DialogUI, InputUI } from '../Interface';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import DescriptionIcon from '@mui/icons-material/Description';
import { sxConfig } from './workSheetCreator.style';

const OperationDialog = ({
  open,
  handleClose,
  data = [],
  valuesState,
  selectedGroupId,
  setDescDialogData,
  operationsState,
}) => {
  const { setValues } = valuesState;
  const { selectedOperations, setSelectedOperations } = operationsState;
  const [filter, setFilter] = useState('');

  const filteredData = useMemo(
    () => data.filter((operation) => operation.name.toLowerCase().includes(filter.toLowerCase())),
    [data, filter]
  );

  const buildSelected = (operation) => {
    return {
      id: uuidv4(),
      selectedGroupId,
      name: operation.name,
      serviceOperationId: operation.id,
      plannedTime: operation.standardTime,
      attachRequired: operation.attachRequired,
      descriptionRequired: operation.descriptionRequired,
    };
  };

  const handleSelect = (operation) => {
    setSelectedOperations((prev) => {
      const exists = prev.some(
        (op) => op.selectedGroupId === selectedGroupId && op.serviceOperationId === operation.id
      );
      if (exists) {
        return prev.filter((op) => !(op.selectedGroupId === selectedGroupId && op.serviceOperationId === operation.id));
      }
      return [...prev, buildSelected(operation)];
    });
  };

  const onClose = () => {
    setFilter('');
    setSelectedOperations([]);
    handleClose();
  };

  const handleSubmit = () => {
    const selectedInGroup = selectedOperations.filter((op) => op.selectedGroupId === selectedGroupId);

    const newMaterials = [];
    const newChecklists = [];

    for (const task of selectedInGroup) {
      const original = data.find((x) => x.id === task.serviceOperationId);
      if (!original) continue;

      if (original.serviceMaterial && original.serviceMaterial.length > 0) {
        newMaterials.push(
          ...original.serviceMaterial.map((item) => ({
            ...item,
            id: uuidv4(),
            serviceTaskId: task.id,
          }))
        );
      }

      if (original.serviceCheckList && original.serviceCheckList.length > 0) {
        newChecklists.push(
          ...original.serviceCheckList.map((item) => ({
            ...item,
            id: uuidv4(),
            serviceTaskId: task.id,
          }))
        );
      }
    }

    setValues((prev) => {
      const prevTasks = prev.serviceTask || [];
      const prevMaterials = prev.serviceMaterial || [];
      const prevChecklists = prev.serviceTaskCheckList || [];

      return {
        ...prev,
        serviceTask: [...prevTasks, ...selectedInGroup],
        serviceMaterial: [
          ...prevMaterials.filter((m) => !selectedInGroup.some((t) => t.id === m.serviceTaskId)),
          ...newMaterials,
        ],
        serviceTaskCheckList: [
          ...prevChecklists.filter((c) => !selectedInGroup.some((t) => t.id === c.serviceTaskId)),
          ...newChecklists,
        ],
      };
    });
    onClose();
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent="Tevékenységlista"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': { width: '100%', maxWidth: '500px', mx: 1 },
        },
        '& .MuiDialogContent-root': {
          px: 1,
        },
      }}
    >
      <Box mt="0.5rem">
        <InputUI
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Keresés..."
          value={filter}
          autoFocus
          size="small"
        />
        {filteredData?.map((operation) => {
          const selectedOp = selectedOperations.find(
            (op) => op.selectedGroupId === selectedGroupId && op.serviceOperationId === operation.id
          );

          const checked = !!selectedOp;
          const note = selectedOp?.note ?? '';

          return (
            <Box key={`${selectedGroupId}-${operation.id}`} mt="0.5rem" display="flex" alignItems="center">
              {checked && (
                <Badge
                  color="primary"
                  variant="dot"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  invisible={!note}
                >
                  <DescriptionIcon
                    sx={sxConfig.operationDialogIcon}
                    onClick={() =>
                      setDescDialogData({
                        open: true,
                        taskId: selectedOp.id,
                        note,
                      })
                    }
                  />
                </Badge>
              )}
              <CheckboxUI
                label={operation.name}
                sx={{ m: 0, p: 0 }}
                onChange={() => handleSelect(operation)}
                checked={checked}
              />
            </Box>
          );
        })}

        <Box display="flex" gap="0.5rem" justifyContent="center" mt="1rem" mb="-1rem">
          <ButtonUI text="Rögzítés" className="bg-success" size="sm" onClick={handleSubmit} />
          <ButtonUI text="Bezárás" className="bg-primary" size="sm" onClick={onClose} />
        </Box>
      </Box>
    </DialogUI>
  );
};

export default OperationDialog;
